import { dbToGain, clamp } from './utils.js';

/**
 * @typedef {"bar" | "beat" | "sub"} StepRole
 */

/**
 * @typedef {object} ClickParams
 * @property {StepRole} [role]
 * @property {0 | 1 | 2} [stressLevel]
 * @property {number} primaryPitchHz
 * @property {number} secondaryPitchHz
 * @property {number} unstressedPitchHz
 * @property {number} primaryDecayMs
 * @property {number} secondaryDecayMs
 * @property {number} unstressedDecayMs
 * @property {number} primaryStressVolPct
 * @property {number} secondaryStressVolPct
 * @property {number} unstressedVolPct
 * @property {(role: StepRole, baseHz: number) => number} roleFrequencyHz
 * @property {(role: StepRole, baseDecaySec: number) => number} roleDecaySeconds
 */

/**
 * @typedef {object} AudioDeps
 * @property {any} el
 * @property {((msg: string) => void) | null} logLine
 * @property {(() => number) | null} getScheduleAheadTime
 */

/** @type {AudioContext | null} */
let audioCtx = null;
/** @type {GainNode | null} */
let masterGain = null;
let audioUnlocked = false;
/** @type {AudioDeps} */
let deps = {
    el: null,
    logLine: null,
    getScheduleAheadTime: null,
};

/** @param {AudioDeps} depsIn */
export function initAudio({ el, logLine, getScheduleAheadTime }) {
    deps = { el, logLine, getScheduleAheadTime };
}

export function getAudioCtx() {
    return audioCtx;
}

/** @param {number} db */
export function setMasterGainDb(db) {
    if (!masterGain || !audioCtx) return;
    const g = dbToGain(db);
    masterGain.gain.setTargetAtTime(g, audioCtx.currentTime, 0.01);
}

export function ensureAudio() {
    if (audioCtx) return;

    const webkitWindow = /** @type {Window & { webkitAudioContext?: typeof AudioContext }} */ (
        window
    );
    const AudioContextCtor = window.AudioContext || webkitWindow.webkitAudioContext;
    if (!AudioContextCtor) return;
    audioCtx = new AudioContextCtor();

    masterGain = audioCtx.createGain();
    masterGain.gain.value = dbToGain(-10);
    masterGain.connect(audioCtx.destination);

    if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
    }

    if (deps.el && deps.el.aheadLabel && deps.getScheduleAheadTime) {
        deps.el.aheadLabel.textContent = String(Math.round(deps.getScheduleAheadTime() * 1000));
    }
    if (deps.logLine) {
        deps.logLine(`Audio initialized @ ${audioCtx.sampleRate} Hz`);
    }
}

export function unlockAudioFromGesture() {
    if (audioUnlocked) return;
    ensureAudio();
    if (!audioCtx || !masterGain) return;
    audioCtx.resume().catch(() => {});
    const osc = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    g.gain.value = 0.0001;
    osc.connect(g);
    g.connect(masterGain);
    const now = audioCtx.currentTime;
    osc.start(now);
    osc.stop(now + 0.02);
    audioUnlocked = true;
}

export function playUiTick() {
    ensureAudio();
    if (!audioCtx || !masterGain) return;
    const now = audioCtx.currentTime;
    const osc = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 1200;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.03, now + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
    osc.connect(g);
    g.connect(masterGain);
    osc.start(now);
    osc.stop(now + 0.04);
}

/** @param {number} pct */
function pctToGainDbCurve(pct) {
    const p = clamp(pct, 0, 100) / 100;
    if (p === 0) return 0;
    const minDb = -60;
    const db = minDb + (0 - minDb) * p;
    return Math.pow(10, db / 20);
}

/** @param {number} time */
export function scheduleDownbeatTone(time) {
    if (!audioCtx || !masterGain) return;
    const osc = audioCtx.createOscillator();
    const g = audioCtx.createGain();

    const attack = 0.003;
    const decay = 0.75;
    const fStart = 140;
    const fEnd = 95;

    osc.type = 'sine';
    osc.frequency.setValueAtTime(fStart, time);
    osc.frequency.exponentialRampToValueAtTime(fEnd, time + decay);

    const basePeak = 0.85;
    const lvl = pctToGainDbCurve(80);
    const peak = Math.min(0.95, basePeak * lvl);

    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(peak, time + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, time + decay);

    osc.connect(g);
    g.connect(masterGain);

    osc.start(time);
    osc.stop(time + decay + 0.02);
}

/** @param {number} time @param {ClickParams} param1 */
export function scheduleClick(
    time,
    {
        role = 'sub',
        stressLevel = 2,
        primaryPitchHz,
        secondaryPitchHz,
        unstressedPitchHz,
        primaryDecayMs,
        secondaryDecayMs,
        unstressedDecayMs,
        primaryStressVolPct,
        secondaryStressVolPct,
        unstressedVolPct,
        roleFrequencyHz,
        roleDecaySeconds,
    },
) {
    if (!audioCtx || !masterGain) return;

    const osc = audioCtx.createOscillator();
    const g = audioCtx.createGain();

    osc.type = 'sine';
    const baseHz =
        stressLevel === 2
            ? primaryPitchHz
            : stressLevel === 1
              ? secondaryPitchHz
              : unstressedPitchHz;
    osc.frequency.value = roleFrequencyHz(role, baseHz);

    const basePeak = 0.78;
    const primaryGain = pctToGainDbCurve(primaryStressVolPct);
    const unstressedGain = pctToGainDbCurve(unstressedVolPct);
    const secondaryGain = pctToGainDbCurve(secondaryStressVolPct);
    const gMul =
        stressLevel === 2 ? primaryGain : stressLevel === 1 ? secondaryGain : unstressedGain;
    let peak = basePeak * gMul;
    peak = Math.min(0.95, peak);

    const attack = 0.001;
    const baseDecayMs =
        stressLevel === 2
            ? primaryDecayMs
            : stressLevel === 1
              ? secondaryDecayMs
              : unstressedDecayMs;
    const decay = roleDecaySeconds(role, baseDecayMs / 1000);

    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(peak, time + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, time + decay);

    osc.connect(g);
    g.connect(masterGain);

    osc.start(time);
    osc.stop(time + decay + 0.02);
}
